/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { debounce } from 'lodash'
import React, { type FC, useState } from 'react'
import { useIntl } from 'react-intl'

import type { EuiComboBoxOptionOption, EuiTableFieldDataColumnType, Search } from '@elastic/eui'
import { EuiComboBox, EuiInMemoryTable, EuiText } from '@elastic/eui'

import messages, { roleMessages } from './messages'
import { CCM_ROLES } from './constants'

import type { CCMRole, CCMRoleAssignment, CCMSearchableTableProps, ConnectedCluster } from './types'

type CCMComboBoxRoleOption = EuiComboBoxOptionOption<CCMRole>

const DEBOUNCE_DELAY_MS = 300

const debounced = debounce((cb) => cb(), DEBOUNCE_DELAY_MS)

const CCMAssignmentsSearchableTable: FC<CCMSearchableTableProps> = ({
  items,
  onChangeCCMRoleAssignments,
}) => {
  const { formatMessage } = useIntl()

  const ccmRoleOptions = CCM_ROLES.map((role) => ({
    value: role,
    text: formatMessage(roleMessages[role]),
  }))

  const ccmRoleColumns: Array<EuiTableFieldDataColumnType<CCMRoleAssignment>> = [
    {
      name: formatMessage(messages.clusterTableColumnLabel),
      field: 'connectedCluster',
      render: (connectedCluster: ConnectedCluster) => (
        <EuiText size='s'>{connectedCluster.name}</EuiText>
      ),
      truncateText: true,
      width: '50%',
    },
    {
      name: formatMessage(messages.rolesTableColumnLabel),
      field: 'roles',
      render: (roles: CCMRole[], ccmRoleAssignment: CCMRoleAssignment) => (
        <EuiComboBox<CCMRole>
          options={ccmRoleOptions.map<CCMComboBoxRoleOption>((option) => ({
            label: option.text,
            value: option.value,
          }))}
          selectedOptions={roles.map<CCMComboBoxRoleOption>((role: CCMRole) => ({
            label: formatMessage(roleMessages[role]),
            value: role,
          }))}
          onChange={(options: CCMComboBoxRoleOption[]) => {
            const selectedRoles = options.map((option) => option.value as CCMRole)

            onChangeCCMRoleAssignments({
              ...ccmRoleAssignment,
              roles: selectedRoles,
            })
          }}
          isClearable={true}
          data-test-subj='ccm-role-combo-box'
          aria-label={`Select roles for Connected cluster ${ccmRoleAssignment.connectedCluster.name}`}
        />
      ),
      width: '50%',
    },
  ]

  const [filterQuery, setFilterQuery] = useState('')

  const filteredItems = items.filter((item) =>
    item.connectedCluster.name.toLowerCase().includes(filterQuery.toLowerCase()),
  )

  const search: Search = {
    onChange: ({ query }) => debounced(() => setFilterQuery(query?.text ?? '')),
    box: {
      incremental: true,
    },
  }

  return (
    <EuiInMemoryTable<CCMRoleAssignment>
      items={filteredItems}
      columns={ccmRoleColumns}
      pagination={true}
      search={search}
      data-test-subj='ccmRoleAssignmentsTable'
    />
  )
}

export default CCMAssignmentsSearchableTable
